import { useMemo } from 'react';

import type { ShippingMethodModel } from '@snowit/types/shippingMethods';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useAuth from '../useAuth';
import useSchemas from '../useSchemas';

export default () => {
  const firebase = useFirebase();
  const { authType, claims } = useAuth();
  const { store, setStore } = useStore();
  const {
    // createDocument,
    // editDocument,
    // writeSubDocument,
    writeDocument,
    deleteDocument,
  } = useSchemas();

  const shippingMethods = useMemo((): ShippingMethodModel[] => {
    return store.shipping_methods.list;
  }, [claims, authType, store.shipping_methods.list]);

  const getShippingMethods = async (): Promise<ShippingMethodModel[]> => {
    try {
      const res = await firebase.query<ShippingMethodModel>({
        collectionName: 'shippingMethods',
        filters: (q) => q.limit(0),
      });
      setStore('shipping_methods', { list: res });
      return res;
    } catch (error) {
      console.error('shipping_methods_get', { error });
      return [];
    } finally {
      setStore('shipping_methods', { ready: true });
    }
  };

  const getShippingMethod = async (
    shippingUID: string,
  ): Promise<ShippingMethodModel | undefined> => {
    try {
      const doc = await firebase.doc<ShippingMethodModel>({
        collectionName: 'shippingMethods',
        id: shippingUID,
      });
      if (!doc) throw new Error('no document found');
      setStore('shipping_methods', {
        list: store.shipping_methods.list.map((it) => (it.uid === doc.uid ? doc : it)),
      });
      return doc;
    } catch (error) {
      console.error({ error });
      return undefined;
    }
  };

  const writeShippingMethod = async (
    shippingUID: string | undefined,
    data: Partial<ShippingMethodModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.SHIPPINGMETHOD,
        attributes: omitter(data),
        id: isNewEntity(shippingUID) ? undefined : shippingUID,
      });
      return id;
    } catch (error: any) {
      console.error('writeShippingMethod', { error });
    }
  };

  const deleteShippingMethod = async (shippingUID: string): Promise<boolean> => {
    try {
      if (isNewEntity(shippingUID)) return false;
      await deleteDocument({
        modelName: SchemaNames.SHIPPINGMETHOD,
        id: shippingUID,
      });
      return true;
    } catch (error: any) {
      console.error('deleteShippingMethod', { error });
      return false;
    }
  };

  return {
    getShippingMethods,
    getShippingMethod,
    writeShippingMethod,
    deleteShippingMethod,
    shippingMethods,
    ready: store.shipping_methods.ready,
  };
};
